/**
 * The audio engine (audio-direction §2, §4.6, §6, §7). Graph:
 *
 *   one-shot voice slot:  source → gain → low-pass → stereo pan ─┬→ hull chain (own ship)
 *                                                               ├→ sfx bus   (near and distant)
 *                                                               └→ ui bus    (cockpit tones, alarms, UI)
 *   loops (drive, boost, lock; M2 tools):  source → gain → low-pass → hull chain / ui bus (lock)
 *   hull chain:  low-shelf (+dB × hull effect) → low-pass → dry ─────────────┐
 *                                                     └→ convolver (cockpit IR) → wet ─┴→ sfx bus
 *   music in → duck → music bus ─┐
 *   sfx bus ─────────────────────┤
 *   ui bus ──────────────────────┼→ night compressor → make-up → mono fold → limiter → clipper (−1 dBFS) → master
 *   comms in → comms bus ────────┘
 *
 * - 48 voices in total (loops reserve theirs), per-sound caps and the priority order (voices.ts).
 * - Positional plays pick a band from the camera distance (spatial.ts): near sounds are low-passed with distance
 *   and panned relative to the camera (updated every frame while they sound), distant events become a muffled
 *   thump plus a rate-limited directional sensor tick, and nothing is heard beyond 20 km.
 * - Variations cycle per sound, with a random pitch (±3 %) and gain (±1.5 dB) per play.
 * - Music ducks −8 dB under comms and −4 dB for 400 ms under big impacts (ducking.ts), evaluated in `update`.
 * - Settings: bus volumes, hull effect intensity, night mode (compression) and mono.
 *
 * Works on a live `AudioContext` or an `OfflineAudioContext` (the WAV previews): every call takes an optional
 * context time. Per play, one `AudioBufferSourceNode` is created (Web Audio sources are single-use; the one
 * accepted allocation, as in M1). `update` and the play paths allocate nothing else (D-014).
 */
import { createRng, nextFloat } from '../core/rng'
import { createDuckState, duckDb, triggerImpactDuck } from './ducking'
import { createIR } from './ir'
import type { SfxBank } from './sfx/bank'
import { dbToGain } from './sfx/dsp'
import { SOUND_IDS, soundIndex, UI_SOUND_IDS, type SfxConfig, type UiSoundId } from './sfxSchema'
import { BAND, bandOf, createListener, createPlacement, distantGain, nearGain, nearLowpass, panOf, place, type Listener } from './spatial'
import { ACQUIRE, acquireVoice, activeVoices, createVoicePool, PRIORITY, releaseVoice, type Priority, type VoicePool } from './voices'
import { renderZzfx } from './zzfx'

/** Where a non-positional play goes. */
export const ROUTE = Object.freeze({ hull: 0, ui: 1, sfx: 2 })
export type Route = (typeof ROUTE)[keyof typeof ROUTE]

/** The persistent loops (M3 R11 appends the cruise hum; M2 appends the tool loops: mining beam, fracture strain, cutting beam, tractor). */
export const LOOP = Object.freeze({ drive: 0, boost: 1, lock: 2, reachCruise: 3, miningBeam: 4, miningStrain: 5, cuttingBeam: 6, tractor: 7 })
export type LoopId = (typeof LOOP)[keyof typeof LOOP]
/** The sound of each loop, by `LOOP` id (`engine.loopVoices` reserves one voice each). */
export const LOOP_SOUNDS = ['driveLoop', 'boostLoop', 'lockSolid', 'reachCruise', 'miningBeamLoop', 'miningStrainLoop', 'cuttingBeamLoop', 'tractorLoop'] as const

export type BusName = 'music' | 'sfx' | 'ui' | 'comms' | 'master'

/** The player's audio settings (audio-direction §6); a structural subset of the game's `Settings`. */
export interface AudioSettings {
  masterVolume: number
  musicVolume: number
  sfxVolume: number
  uiVolume: number
  commsVolume: number
  /** Hull effect intensity 0..1. */
  hullEffect: number
  nightMode: boolean
  mono: boolean
}

export interface EngineStats {
  plays: number
  steals: number
  /** Plays refused by the voice limiter (priority too low for a full pool). */
  drops: number
  /** Plays of sounds not rendered yet. */
  skipped: number
  distantThumps: number
  ticks: number
}

export interface AudioEngine {
  readonly ac: BaseAudioContext
  readonly bank: SfxBank
  readonly listener: Listener
  readonly voices: VoicePool
  readonly stats: EngineStats
  /** Inputs of the music and comms buses (Tone.js and the radio chain connect here). */
  readonly musicIn: AudioNode
  readonly commsIn: AudioNode
  /** Nodes to tap for meters. */
  readonly taps: Readonly<Record<BusName, AudioNode>>
  /**
   * Own ship, cockpit tones and alarms: not positional (an optional fixed pan, e.g. a side RCS puff). `rate`
   * scales the playback rate (the rising lock beep). Returns the voice slot, or -1.
   */
  play(sound: number, gain: number, route: Route, priority: Priority, pan?: number, rate?: number, when?: number): number
  /** A sound at a float64 world point: near (filtered, panned), distant (thump + tick) or silent. */
  playWorld(sound: number, gain: number, x: number, y: number, z: number, priority: Priority, when?: number): number
  playUi(id: UiSoundId, when?: number): void
  /**
   * Additive (M3 R11): stops a one-shot early with the steal fade, if `slot` still plays `sound` (a cancelled
   * cruise charge). A slot that has ended or moved on to another sound is left alone.
   */
  stopVoice(slot: number, sound: number, when?: number): void
  /** Starts, updates or stops a persistent loop (gain 0..2, playback rate, low-pass Hz). */
  setLoop(loop: LoopId, on: boolean, gain: number, rate: number, cutoff: number, when?: number): void
  /** Starts the −4 dB / 400 ms impact duck. */
  duckImpact(when?: number): void
  /** A comms line goes on (+1) or off (−1) air. */
  commsOnAir(delta: 1 | -1): void
  applySettings(s: AudioSettings, when?: number): void
  /** Per frame: ducking and the pan/filter of moving-camera positional voices. */
  update(now?: number): void
  activeVoices(now?: number): number
  stopAll(when?: number): void
  dispose(): void
}

const NEUTRAL_CUTOFF = 20000

/** A soft-knee ceiling curve: linear up to `knee × ceiling`, then a tanh approach to `ceiling` (never above). */
export function ceilingCurve(ceilingDb: number, kneeStart: number, points = 4096): Float32Array<ArrayBuffer> {
  const c = dbToGain(ceilingDb)
  const k = c * kneeStart
  const curve = new Float32Array(points)
  for (let i = 0; i < points; i++) {
    const x = (i / (points - 1)) * 2 - 1
    const a = Math.abs(x)
    const y = a <= k ? a : k + (c - k) * Math.tanh((a - k) / (c - k))
    curve[i] = x < 0 ? -y : y
  }
  return curve
}

function logLerp(a: number, b: number, u: number): number {
  return a * Math.pow(b / a, u < 0 ? 0 : u > 1 ? 1 : u)
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}

export function createAudioEngine(ac: BaseAudioContext, bank: SfxBank, config: SfxConfig): AudioEngine {
  const e = config.engine
  const rng = createRng(config.render.seed ^ 0x5eed)
  const soundCount = SOUND_IDS.length
  const caps: number[] = SOUND_IDS.map((id) => config.sounds[id].mix.voices)
  for (const id of UI_SOUND_IDS) caps.push(config.ui[id].voices)
  const pool = createVoicePool(e.maxVoices - e.loopVoices, caps)
  const listener = createListener()
  const placement = createPlacement()
  const duck = createDuckState()
  const stats: EngineStats = { plays: 0, steals: 0, drops: 0, skipped: 0, distantThumps: 0, ticks: 0 }
  const t = (when?: number): number => (when === undefined ? ac.currentTime : when)

  // ----- master chain -----
  const master = ac.createGain()
  master.gain.value = 0
  master.connect(ac.destination)
  const clipper = ac.createWaveShaper()
  clipper.curve = ceilingCurve(e.master.ceilingDb, e.master.kneeStart)
  clipper.oversample = '4x'
  clipper.connect(master)
  const limiter = ac.createDynamicsCompressor()
  limiter.threshold.value = e.master.limiter.thresholdDb
  limiter.knee.value = e.master.limiter.kneeDb
  limiter.ratio.value = e.master.limiter.ratio
  limiter.attack.value = e.master.limiter.attack
  limiter.release.value = e.master.limiter.release
  limiter.connect(clipper)
  const monoFold = ac.createGain()
  monoFold.channelInterpretation = 'speakers'
  monoFold.connect(limiter)
  const makeup = ac.createGain()
  makeup.connect(monoFold)
  const night = ac.createDynamicsCompressor()
  night.threshold.value = 0
  night.knee.value = 0
  night.ratio.value = 1
  night.connect(makeup)
  const preMaster = ac.createGain()
  preMaster.connect(night)

  // ----- buses -----
  const musicBus = ac.createGain()
  musicBus.connect(preMaster)
  const musicDuck = ac.createGain()
  musicDuck.connect(musicBus)
  const sfxBus = ac.createGain()
  sfxBus.connect(preMaster)
  const uiBus = ac.createGain()
  uiBus.connect(preMaster)
  const commsBus = ac.createGain()
  commsBus.connect(preMaster)
  for (const b of [musicBus, sfxBus, uiBus, commsBus]) b.gain.value = 0

  // ----- hull chain -----
  const hullIn = ac.createGain()
  const shelf = ac.createBiquadFilter()
  shelf.type = 'lowshelf'
  shelf.frequency.value = e.hull.lowShelfHz
  const hullLp = ac.createBiquadFilter()
  hullLp.type = 'lowpass'
  hullLp.Q.value = e.hull.q
  const hullDry = ac.createGain()
  hullDry.gain.value = e.hull.dry
  const conv = ac.createConvolver()
  conv.normalize = true
  conv.buffer = createIR(ac, e.hull.ir, config.render.seed ^ 0xc0c)
  const hullWet = ac.createGain()
  hullIn.connect(shelf)
  shelf.connect(hullLp)
  hullLp.connect(hullDry)
  hullDry.connect(sfxBus)
  hullLp.connect(conv)
  conv.connect(hullWet)
  hullWet.connect(sfxBus)

  // ----- one-shot voice slots -----
  const n = pool.size
  const slotGain: GainNode[] = []
  const slotFilter: BiquadFilterNode[] = []
  const slotPan: StereoPannerNode[] = []
  for (let i = 0; i < n; i++) {
    const g = ac.createGain()
    const f = ac.createBiquadFilter()
    f.type = 'lowpass'
    f.frequency.value = NEUTRAL_CUTOFF
    const p = ac.createStereoPanner()
    g.connect(f)
    f.connect(p)
    slotGain.push(g)
    slotFilter.push(f)
    slotPan.push(p)
  }
  const sources: (AudioBufferSourceNode | null)[] = new Array<AudioBufferSourceNode | null>(n).fill(null)
  const target = new Int8Array(n).fill(-1)
  const positional = new Uint8Array(n)
  const posX = new Float64Array(n)
  const posY = new Float64Array(n)
  const posZ = new Float64Array(n)
  const lastPan = new Float32Array(n)
  const lastCut = new Float32Array(n)
  const nextTake = new Int32Array(caps.length)
  let lastTick = -Infinity
  const routeNode: AudioNode[] = [hullIn, uiBus, sfxBus]

  // ----- UI sounds (ZzFX, rendered here: tiny) -----
  const uiTakes: AudioBuffer[][] = UI_SOUND_IDS.map((id) => {
    const samples = renderZzfx(config.ui[id].zzfx, ac.sampleRate, () => nextFloat(rng))
    const b = ac.createBuffer(1, Math.max(1, samples.length), ac.sampleRate)
    b.getChannelData(0).set(samples)
    return [b]
  })

  // ----- loops -----
  const loopGain: GainNode[] = []
  const loopFilter: BiquadFilterNode[] = []
  const loopSrc: (AudioBufferSourceNode | null)[] = new Array<AudioBufferSourceNode | null>(LOOP_SOUNDS.length).fill(null)
  const loopLast = new Float32Array(LOOP_SOUNDS.length * 3).fill(-1)
  const m2 = config.m2
  const drive = config.ship.drive.smoothing
  const loopAttack = [e.volumeSmoothing, config.ship.boostLoop.attack, e.stealFadeSeconds, drive, m2.loopAttack, m2.loopAttack, m2.loopAttack, m2.loopAttack]
  const loopRelease = [drive, config.ship.boostLoop.release, e.stealFadeSeconds, drive, m2.loopRelease, m2.loopRelease, m2.loopRelease, m2.loopRelease]
  const loopSmoothing = [drive, drive, e.stealFadeSeconds, drive, m2.loopSmoothing, m2.loopSmoothing, m2.loopSmoothing, m2.loopSmoothing]
  for (let i = 0; i < LOOP_SOUNDS.length; i++) {
    const g = ac.createGain()
    g.gain.value = 0
    const f = ac.createBiquadFilter()
    f.type = 'lowpass'
    f.frequency.value = NEUTRAL_CUTOFF
    g.connect(f)
    f.connect(i === LOOP.lock ? uiBus : hullIn)
    loopGain.push(g)
    loopFilter.push(f)
  }

  function connectSlot(slot: number, route: number): void {
    if (target[slot] === route) return
    if (target[slot] >= 0) slotPan[slot].disconnect()
    slotPan[slot].connect(routeNode[route])
    target[slot] = route
  }

  function takesOf(sound: number): readonly AudioBuffer[] | null {
    return sound < soundCount ? bank.takes[sound] : uiTakes[sound - soundCount]
  }

  /** Fades the voice in `slot` out from `at` and stops it at the end of the fade. Returns that time. */
  function fadeOut(slot: number, at: number): number {
    const end = at + e.stealFadeSeconds
    const g = slotGain[slot].gain
    g.cancelScheduledValues(at)
    g.setValueAtTime(g.value, at)
    g.linearRampToValueAtTime(0, end)
    const src = sources[slot]
    if (src !== null) src.stop(end)
    sources[slot] = null
    return end
  }

  function setParam(p: AudioParam, v: number, at: number): void {
    p.cancelScheduledValues(at)
    p.setValueAtTime(v, at)
  }

  /** Starts `sound` in a slot; route 0..2 as ROUTE. Returns the slot or -1. */
  function start(sound: number, gain: number, route: number, priority: Priority, at: number, pan: number, cutoff: number, rateScale: number): number {
    const takes = takesOf(sound)
    if (takes === null) {
      stats.skipped++
      return -1
    }
    const ui = sound >= soundCount
    const mix = ui ? null : bank.defs[sound].mix
    const jitterDb = mix === null ? 0 : (nextFloat(rng) * 2 - 1) * mix.gainJitterDb
    const level = gain * (mix === null ? config.ui[UI_SOUND_IDS[sound - soundCount]].gain : mix.gain) * dbToGain(jitterDb)
    if (!(level > 0)) return -1
    const take = nextTake[sound]
    nextTake[sound] = take + 1 >= takes.length ? 0 : take + 1
    const buffer = takes[take]
    const rate = rateScale * (mix === null || mix.pitchJitter <= 0 ? 1 : 1 + (nextFloat(rng) * 2 - 1) * mix.pitchJitter)
    const slot = acquireVoice(pool, sound, priority, at, buffer.duration / rate)
    if (slot < 0) {
      stats.drops++
      return -1
    }
    let t0 = at
    if (pool.result !== ACQUIRE.free && sources[slot] !== null) {
      stats.steals++
      t0 = fadeOut(slot, at)
      pool.start[slot] = t0
      pool.end[slot] = t0 + buffer.duration / rate
    } else {
      slotGain[slot].gain.cancelScheduledValues(at)
    }
    slotGain[slot].gain.setValueAtTime(level, t0)
    setParam(slotPan[slot].pan, pan, t0)
    setParam(slotFilter[slot].frequency, cutoff, t0)
    lastPan[slot] = pan
    lastCut[slot] = cutoff
    connectSlot(slot, route)
    const src = ac.createBufferSource()
    src.buffer = buffer
    src.playbackRate.value = rate
    src.connect(slotGain[slot])
    src.start(t0)
    sources[slot] = src
    positional[slot] = 0
    stats.plays++
    // Only a big, close impact ducks the music (a far explosion is not "big" at the listener).
    if (mix !== null && mix.duck && gain >= e.ducking.impactMinGain) triggerImpactDuck(duck, t0, e.ducking)
    return slot
  }

  const applied = { master: -1, music: -1, sfx: -1, ui: -1, comms: -1, hull: -1, night: -1, mono: -1, duck: 0 }

  function smooth(p: AudioParam, v: number, at: number, seconds: number): void {
    p.setTargetAtTime(v, at, Math.max(seconds, 1e-3) / 3)
  }

  const engine: AudioEngine = {
    ac,
    bank,
    listener,
    voices: pool,
    stats,
    musicIn: musicDuck,
    commsIn: commsBus,
    taps: { music: musicBus, sfx: sfxBus, ui: uiBus, comms: commsBus, master },

    play(sound: number, gain: number, route: Route, priority: Priority, pan = 0, rate = 1, when?: number): number {
      return start(sound, gain, route, priority, t(when), pan, NEUTRAL_CUTOFF, rate)
    },

    playWorld(sound: number, gain: number, x: number, y: number, z: number, priority: Priority, when?: number): number {
      const at = t(when)
      place(listener, x, y, z, placement)
      const d = placement.distance
      const band = bandOf(d, config.spatial, config.falloff)
      if (band === BAND.silent) return -1
      const pan = panOf(placement, config.spatial)
      if (band === BAND.near) {
        const g = gain * nearGain(d, config.falloff)
        if (g < config.falloff.minGain) return -1
        const slot = start(sound, g, ROUTE.sfx, priority, at, pan, nearLowpass(d, placement.behind, config.spatial, config.falloff), 1)
        if (slot >= 0) {
          positional[slot] = 1
          posX[slot] = x
          posY[slot] = y
          posZ[slot] = z
        }
        return slot
      }
      // Distant: the sound's muffled thump, and (for big events) a directional sensor tick in the cockpit.
      const dist = sound < soundCount ? bank.defs[sound].mix.distant : null
      if (dist === null) return -1
      const thump = soundIndex(dist.sound)
      const g = gain * dist.gain * distantGain(d, config.spatial, config.distant, config.falloff)
      const slot = start(thump, g, ROUTE.sfx, PRIORITY.distant, at, pan, config.distant.lowpassHz, 1)
      if (slot >= 0) stats.distantThumps++
      if (dist.tick && at - lastTick >= config.distant.tick.minInterval) {
        lastTick = at
        if (start(soundIndex(config.distant.tick.sound), config.distant.tick.gain, ROUTE.ui, PRIORITY.alarm, at, pan, NEUTRAL_CUTOFF, 1) >= 0) stats.ticks++
      }
      return slot
    },

    playUi(id: UiSoundId, when?: number): void {
      const k = UI_SOUND_IDS.indexOf(id)
      if (k >= 0) start(soundCount + k, 1, ROUTE.ui, PRIORITY.alarm, t(when), 0, NEUTRAL_CUTOFF, 1)
    },

    stopVoice(slot: number, sound: number, when?: number): void {
      const at = t(when)
      if (slot < 0 || slot >= n || pool.sound[slot] !== sound || at >= pool.end[slot] || sources[slot] === null) return
      pool.end[slot] = fadeOut(slot, at)
    },

    setLoop(loop: LoopId, on: boolean, gain: number, rate: number, cutoff: number, when?: number): void {
      const at = t(when)
      const g = loopGain[loop]
      const k = loop * 3
      const src = loopSrc[loop]
      if (!on) {
        if (src === null) return
        g.gain.cancelScheduledValues(at)
        g.gain.setValueAtTime(g.gain.value, at)
        smooth(g.gain, 0, at, loopRelease[loop])
        src.stop(at + loopRelease[loop] * 2)
        loopSrc[loop] = null
        loopLast[k] = -1
        return
      }
      if (src === null) {
        const takes = bank.takes[soundIndex(LOOP_SOUNDS[loop])]
        if (takes === null) return
        const s = ac.createBufferSource()
        s.buffer = takes[0]
        s.loop = true
        s.playbackRate.value = rate
        s.connect(g)
        g.gain.cancelScheduledValues(at)
        g.gain.setValueAtTime(0, at)
        smooth(g.gain, gain * bank.defs[soundIndex(LOOP_SOUNDS[loop])].mix.gain, at, loopAttack[loop])
        setParam(loopFilter[loop].frequency, cutoff, at)
        s.start(at)
        loopSrc[loop] = s
        loopLast[k] = gain
        loopLast[k + 1] = rate
        loopLast[k + 2] = cutoff
        return
      }
      const eps = 0.005
      if (Math.abs(gain - loopLast[k]) > eps) {
        smooth(g.gain, gain * bank.defs[soundIndex(LOOP_SOUNDS[loop])].mix.gain, at, loopSmoothing[loop])
        loopLast[k] = gain
      }
      if (Math.abs(rate - loopLast[k + 1]) > eps * 0.5) {
        smooth(src.playbackRate, rate, at, loopSmoothing[loop])
        loopLast[k + 1] = rate
      }
      if (Math.abs(cutoff - loopLast[k + 2]) > loopLast[k + 2] * 0.02) {
        smooth(loopFilter[loop].frequency, cutoff, at, loopSmoothing[loop])
        loopLast[k + 2] = cutoff
      }
    },

    duckImpact(when?: number): void {
      triggerImpactDuck(duck, t(when), e.ducking)
    },

    commsOnAir(delta: 1 | -1): void {
      duck.comms = Math.max(0, duck.comms + delta)
    },

    applySettings(s: AudioSettings, when?: number): void {
      const at = t(when)
      const tc = e.volumeSmoothing
      const masterTarget = clamp01(s.masterVolume) * e.masterTrim
      if (masterTarget !== applied.master) {
        smooth(master.gain, masterTarget, at, tc)
        applied.master = masterTarget
      }
      const music = clamp01(s.musicVolume) * e.buses.music
      if (music !== applied.music) {
        smooth(musicBus.gain, music, at, tc)
        applied.music = music
      }
      const sfx = clamp01(s.sfxVolume) * e.buses.sfx
      if (sfx !== applied.sfx) {
        smooth(sfxBus.gain, sfx, at, tc)
        applied.sfx = sfx
      }
      const ui = clamp01(s.uiVolume) * e.buses.ui
      if (ui !== applied.ui) {
        smooth(uiBus.gain, ui, at, tc)
        applied.ui = ui
      }
      const comms = clamp01(s.commsVolume) * e.buses.comms
      if (comms !== applied.comms) {
        smooth(commsBus.gain, comms, at, tc)
        applied.comms = comms
      }
      const h = clamp01(s.hullEffect)
      if (h !== applied.hull) {
        smooth(shelf.gain, e.hull.lowShelfDb * h, at, tc)
        smooth(hullLp.frequency, logLerp(e.hull.lowpassHz, e.hull.lowpassMinHz, h), at, tc)
        smooth(hullWet.gain, e.hull.wet * h, at, tc)
        applied.hull = h
      }
      const nightOn = s.nightMode ? 1 : 0
      if (nightOn !== applied.night) {
        const c = e.master.night
        setParam(night.threshold, s.nightMode ? c.thresholdDb : 0, at)
        setParam(night.knee, s.nightMode ? c.kneeDb : 0, at)
        setParam(night.ratio, s.nightMode ? c.ratio : 1, at)
        setParam(night.attack, c.attack, at)
        setParam(night.release, c.release, at)
        smooth(makeup.gain, s.nightMode ? dbToGain(c.makeupDb) : 1, at, tc)
        applied.night = nightOn
      }
      const monoOn = s.mono ? 1 : 0
      if (monoOn !== applied.mono) {
        monoFold.channelCount = s.mono ? 1 : 2
        monoFold.channelCountMode = s.mono ? 'explicit' : 'max'
        applied.mono = monoOn
      }
    },

    update(now?: number): void {
      const at = t(now)
      const d = duckDb(duck, at, e.ducking)
      if (d !== applied.duck) {
        smooth(musicDuck.gain, dbToGain(d), at, d < applied.duck ? e.ducking.attack : e.ducking.release)
        applied.duck = d
      }
      const th = config.spatial.updateThreshold
      for (let i = 0; i < n; i++) {
        if (positional[i] === 0 || at >= pool.end[i]) continue
        place(listener, posX[i], posY[i], posZ[i], placement)
        const pan = panOf(placement, config.spatial)
        if (Math.abs(pan - lastPan[i]) > th) {
          smooth(slotPan[i].pan, pan, at, 0.05)
          lastPan[i] = pan
        }
        if (bandOf(placement.distance, config.spatial, config.falloff) !== BAND.near) continue
        const cut = nearLowpass(placement.distance, placement.behind, config.spatial, config.falloff)
        if (Math.abs(cut - lastCut[i]) > lastCut[i] * th) {
          smooth(slotFilter[i].frequency, cut, at, 0.05)
          lastCut[i] = cut
        }
      }
    },

    activeVoices(now?: number): number {
      let loops = 0
      for (let i = 0; i < loopSrc.length; i++) if (loopSrc[i] !== null) loops++
      return activeVoices(pool, t(now)) + loops
    },

    stopAll(when?: number): void {
      const at = t(when)
      for (let i = 0; i < n; i++) {
        const src = sources[i]
        if (src !== null) src.stop(at)
        sources[i] = null
        releaseVoice(pool, i)
      }
      for (let i = 0; i < loopSrc.length; i++) {
        const src = loopSrc[i]
        if (src !== null) src.stop(at)
        loopSrc[i] = null
      }
    },

    dispose(): void {
      engine.stopAll()
      master.disconnect()
    },
  }
  return engine
}
